import React, { useState } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { AppData } from '../../types';
import { formatDateTime } from '../../utils/dateFormat';
import { Shield, AlertCircle, FileText, Users, Clock, Activity } from 'lucide-react';
interface DashboardProps {
  data: AppData;
  onSaveSafetyCheck: (check: any) => void;
}
export function Dashboard({
  data,
  onSaveSafetyCheck
}: DashboardProps) {
  const [safetyStatus, setSafetyStatus] = useState<'safe' | 'unsafe' | null>(null);
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const stats = [{
    label: 'Total Incidents',
    value: data.incidents.length,
    icon: <AlertCircle className="w-5 h-5 text-purple-600" data-id="element-45" />
  }, {
    label: 'Evidence Items',
    value: data.evidence.length,
    icon: <FileText className="w-5 h-5 text-blue-600" data-id="element-46" />
  }, {
    label: 'People Documented',
    value: data.people.length,
    icon: <Users className="w-5 h-5 text-green-600" data-id="element-47" />
  }, {
    label: 'Check-ins Logged',
    value: data.checkins.length,
    icon: <Activity className="w-5 h-5 text-pink-600" data-id="element-48" />
  }];
  const recentActivity = [...data.incidents.map(i => ({
    type: 'Incident',
    title: i.title,
    date: i.datetime,
    id: i.id
  })), ...data.checkins.map(c => ({
    type: 'Check-in',
    title: `Mood: ${c.mood}`,
    date: c.timestamp,
    id: c.id
  })), ...data.evidence.map(e => ({
    type: 'Evidence',
    title: e.fileName,
    date: e.timestamp,
    id: e.id
  }))].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()).slice(0, 5);
  const handleSafetyCheck = (status: 'safe' | 'unsafe') => {
    setSafetyStatus(status);
    onSaveSafetyCheck({
      status: status === 'safe' ? 'Safe' : 'Unsafe',
      feelings: [],
      timestamp: new Date().toISOString()
    });
  };
  return <div className="space-y-6 max-w-6xl mx-auto" data-id="element-49">
      {/* Waitlist Card */}
      <div className="bg-primary-gradient rounded-2xl p-8 text-white shadow-lg relative overflow-hidden" data-id="element-50">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-10 rounded-full -mr-16 -mt-16 blur-3xl" data-id="element-51"></div>
        <div className="relative z-10 max-w-2xl" data-id="element-52">
          <h2 className="text-2xl font-bold mb-2" data-id="element-53">
            🚀 Join the Waitlist for Zapiis Pro
          </h2>
          <p className="text-white/90 mb-6" data-id="element-54">
            Get cloud sync, mobile apps, and full HIPAA compliance when we
            launch.
          </p>

          {!submitted ? <div className="flex gap-3 max-w-md" data-id="element-55">
              <input type="email" placeholder="Enter your email" className="flex-1 px-4 py-2 rounded-lg text-gray-900 focus:outline-none" value={email} onChange={e => setEmail(e.target.value)} data-id="element-56" />
              <button onClick={() => setSubmitted(true)} className="bg-white text-primary-purple px-6 py-2 rounded-lg font-bold hover:bg-gray-100 transition-colors" data-id="element-57">
                Join Waitlist
              </button>
            </div> : <div className="bg-white/20 inline-block px-4 py-2 rounded-lg font-medium" data-id="element-58">
              ✅ Thanks for joining! We'll be in touch.
            </div>}
        </div>
      </div>

      {/* Safety Check */}
      <Card className="border-l-4 border-l-blue-500" data-id="element-59">
        <div className="flex items-start gap-4" data-id="element-60">
          <div className="bg-blue-100 p-3 rounded-full" data-id="element-61">
            <Shield className="w-6 h-6 text-blue-600" data-id="element-62" />
          </div>
          <div className="flex-1" data-id="element-63">
            <h3 className="text-lg font-bold text-gray-900 mb-1" data-id="element-64">
              Safety Check
            </h3>
            <p className="text-gray-600 mb-4" data-id="element-65">Are you currently safe?</p>

            <div className="flex gap-3 mb-4" data-id="element-66">
              <Button variant={safetyStatus === 'safe' ? 'primary' : 'outline'} className={safetyStatus === 'safe' ? 'bg-green-600 hover:bg-green-700' : 'text-gray-600 border-gray-300'} onClick={() => handleSafetyCheck('safe')} data-id="element-67">
                Yes, I'm safe
              </Button>
              <Button variant={safetyStatus === 'unsafe' ? 'danger' : 'outline'} className={safetyStatus === 'unsafe' ? '' : 'text-gray-600 border-gray-300 hover:bg-red-50 hover:text-red-600 hover:border-red-200'} onClick={() => handleSafetyCheck('unsafe')} data-id="element-68">
                No, I need help
              </Button>
            </div>

            {safetyStatus === 'unsafe' && <div className="bg-red-50 p-4 rounded-lg border border-red-100 animate-in fade-in slide-in-from-top-2" data-id="element-69">
                <h4 className="font-bold text-red-800 mb-2" data-id="element-70">
                  Crisis Resources
                </h4>
                <ul className="space-y-2 text-sm text-red-700" data-id="element-71">
                  <li data-id="element-72">
                    📞 <strong data-id="element-73">Emergency:</strong> 911
                  </li>
                  <li data-id="element-74">
                    📞 <strong data-id="element-75">Domestic Violence Hotline:</strong>{' '}
                    1-800-799-7233
                  </li>
                  <li data-id="element-76">
                    💬 <strong data-id="element-77">Crisis Text Line:</strong> Text HOME to 741741
                  </li>
                  <li data-id="element-78">
                    📞 <strong data-id="element-79">Suicide Prevention:</strong> 988
                  </li>
                </ul>
              </div>}
          </div>
        </div>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-80">
        {stats.map(stat => <Card key={stat.label} className="flex items-center gap-4" data-id="element-81">
            <div className="p-3 bg-gray-50 rounded-full" data-id="element-82">{stat.icon}</div>
            <div data-id="element-83">
              <p className="text-sm text-gray-500 font-medium" data-id="element-84">{stat.label}</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-85">{stat.value}</p>
            </div>
          </Card>)}
      </div>

      {/* Recent Activity */}
      <Card data-id="element-86">
        <div className="flex items-center justify-between mb-6" data-id="element-87">
          <h3 className="text-lg font-bold text-gray-900" data-id="element-88">Recent Activity</h3>
          <Clock className="w-5 h-5 text-gray-400" data-id="element-89" />
        </div>

        {recentActivity.length > 0 ? <div className="space-y-4" data-id="element-90">
            {recentActivity.map(item => <div key={`${item.type}-${item.id}`} className="flex items-center justify-between py-3 border-b border-gray-50 last:border-0" data-id="element-91">
                <div className="flex items-center gap-3" data-id="element-92">
                  <Badge variant={item.type === 'Incident' ? 'red' : item.type === 'Evidence' ? 'blue' : 'purple'} data-id="element-93">
                    {item.type}
                  </Badge>
                  <span className="font-medium text-gray-900" data-id="element-94">
                    {item.title}
                  </span>
                </div>
                <span className="text-sm text-gray-500" data-id="element-95">
                  {formatDateTime(item.date)}
                </span>
              </div>)}
          </div> : <div className="text-center py-8 text-gray-500" data-id="element-96">
            No activity yet. Start documenting by adding an incident!
          </div>}
      </Card>
    </div>;
}